import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '@material-ui/styles';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  useMediaQuery,
} from '@material-ui/core';
import { Code } from '@material-ui/icons';
import axios from 'axios';
import moment from 'moment';
import Swal from 'sweetalert2';

const NO_TEMPLATE = '';

const emptyScript = {
  id: 0,
  company_id: 0,
  name: '',
  script: '',
  script_based_on_client_script_id: NO_TEMPLATE,
  status_name: '',
  error_message: '',
  created_at: null,
  applied_at: null,
  effective_at: '',
};

const formatDateTime = (value) => (value == null ? '' : moment(value).format('MM/DD/YYYY hh:mm A'));

const jsonHeaders = {
  headers: {
    'Accept': 'application/json',
    'Content-Type': 'application/json'
  }
};

// Add/edit form shared by the client Scripts section (mode "client")
// and the Script Templates module (mode "template").
const ClientScriptDialog = props => {
  const { open, mode, scriptId, clientId, companies, templates, onClose, onSaved } = props;
  const isTemplate = mode === 'template';

  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('xs'));

  const [values, setValues] = useState(emptyScript);
  const [isSaving, setIsSaving] = useState(false);

  const endpoints = isTemplate ? {
    get: '/backend/script_templates/get_script_template',
    save: '/backend/script_templates/save_script_template',
  } : {
    get: '/backend/clients/get_client_script',
    save: '/backend/clients/save_client_script',
  };

  useEffect(() => {
    if (!open) return;

    if (!scriptId) {
      setValues(emptyScript);
      return;
    }

    const loadScript = async () => {
      try {
        const result = await axios.post(endpoints.get, JSON.stringify({ id: scriptId }), jsonHeaders);
        if (result.data.error_message) {
          Swal.fire('Error', result.data.error_message, 'error');
          onClose();
          return;
        }
        setValues({
          ...emptyScript,
          ...result.data,
          script: result.data.script == null ? '' : result.data.script,
          effective_at: result.data.effective_at == null ? '' : moment(result.data.effective_at).format('YYYY-MM-DDTHH:mm'),
          error_message: result.data.script_error_message || '',
          script_based_on_client_script_id: result.data.script_based_on_client_script_id == null ? NO_TEMPLATE : result.data.script_based_on_client_script_id,
        });
      }
      catch (e) {
        Swal.fire('Error', 'Unable to load script.', 'error');
        onClose();
      }
    };

    loadScript();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, scriptId, mode]);

  const handleChange = event => {
    setValues({
      ...values,
      [event.target.name]: event.target.value
    });
  };

  const hasTemplate = !isTemplate && values.script_based_on_client_script_id !== NO_TEMPLATE;

  const handleSave = async () => {
    if (values.name.trim() === '') {
      Swal.fire('Required', 'Name is required.', 'warning');
      return;
    }
    if (!hasTemplate && values.script.trim() === '') {
      Swal.fire('Required', isTemplate ? 'Script is required.' : 'Script is required when no template is selected.', 'warning');
      return;
    }

    const confirm = await Swal.fire({
      title: 'Are you sure?',
      text: 'Save this script?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, save it!',
      cancelButtonText: 'No, cancel!',
      reverseButtons: true
    });
    if (!confirm.value) return;

    const session = await axios.get('/backend/check_session');
    if (Object.keys(session.data).length === 0) {
      window.location.replace('/sign-in');
      return;
    }

    setIsSaving(true);
    try {
      const result = await axios.post(endpoints.save, JSON.stringify({
        id: values.id,
        client_id: clientId,
        company_id: values.company_id,
        name: values.name,
        script: values.script,
        script_based_on_client_script_id: hasTemplate ? values.script_based_on_client_script_id : null,
        effective_at: isTemplate || values.effective_at === '' ? null : values.effective_at,
      }), jsonHeaders);

      if (result.data.error_message) {
        Swal.fire('Error', result.data.error_message, 'error');
        return;
      }

      await Swal.fire('Saved!', 'The script has been saved.', 'success');
      onSaved();
    }
    catch (e) {
      Swal.fire('Error', e.response && e.response.data && e.response.data.error_message ? e.response.data.error_message : 'Unable to save script.', 'error');
    }
    finally {
      setIsSaving(false);
    }
  };

  const isExisting = values.id !== 0;
  const title = `${isExisting ? 'Edit' : 'New'} ${isTemplate ? 'Script Template' : 'Script'}`;

  return (
    <Dialog
      fullWidth={true}
      fullScreen={isXs}
      maxWidth={'md'}
      open={open}
      onClose={onClose}
      aria-labelledby="client-script-dialog-title"
    >
      <DialogTitle id="client-script-dialog-title">{title}{' '}<Code /></DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          {!isTemplate ? (
            <Grid item md={6} xs={12}>
              <InputLabel id="client_script_company_label" shrink>Company</InputLabel>
              <Select
                fullWidth
                labelId="client_script_company_label"
                name="company_id"
                value={values.company_id}
                onChange={handleChange}
              >
                <MenuItem value={0}>ALL</MenuItem>
                {companies.map(company => (
                  <MenuItem key={company.id} value={company.id}>{company.name}</MenuItem>
                ))}
              </Select>
            </Grid>
          ) : null}
          <Grid item md={isTemplate ? 12 : 6} xs={12}>
            <TextField
              fullWidth
              required
              label="Name"
              margin="dense"
              name="name"
              onChange={handleChange}
              value={values.name}
              inputProps={{ maxLength: 100 }}
              variant="outlined"
            />
          </Grid>
          {!isTemplate ? (
            <Grid item md={6} xs={12}>
              <InputLabel id="client_script_template_label" shrink>Template</InputLabel>
              <Select
                fullWidth
                displayEmpty
                labelId="client_script_template_label"
                name="script_based_on_client_script_id"
                value={values.script_based_on_client_script_id}
                onChange={handleChange}
              >
                <MenuItem value={NO_TEMPLATE}>None</MenuItem>
                {templates.map(template => (
                  <MenuItem key={template.id} value={template.id}>{template.name}</MenuItem>
                ))}
              </Select>
            </Grid>
          ) : null}
          {!isTemplate ? (
            <Grid item md={6} xs={12}>
              <TextField
                fullWidth
                type="datetime-local"
                label="Effective At"
                margin="dense"
                name="effective_at"
                onChange={handleChange}
                value={values.effective_at}
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                helperText="Optional. Leave blank to run on the next check."
              />
            </Grid>
          ) : null}
          <Grid item md={12} xs={12}>
            <TextField
              fullWidth
              multiline
              required={!hasTemplate}
              rows={14}
              label={hasTemplate ? 'Script (optional - the template script is used)' : 'Script'}
              margin="dense"
              name="script"
              onChange={handleChange}
              value={values.script}
              variant="outlined"
              inputProps={{ style: { fontFamily: 'Consolas, "Courier New", monospace' }, spellCheck: false }}
            />
          </Grid>
          {isExisting ? (
            <>
              <Grid item md={4} xs={12}>
                <TextField fullWidth label="Status" margin="dense" value={values.status_name || ''} variant="outlined" InputProps={{ readOnly: true }} />
              </Grid>
              <Grid item md={4} xs={12}>
                <TextField fullWidth label="Created At" margin="dense" value={formatDateTime(values.created_at)} variant="outlined" InputProps={{ readOnly: true }} />
              </Grid>
              <Grid item md={4} xs={12}>
                <TextField fullWidth label="Applied At" margin="dense" value={formatDateTime(values.applied_at)} variant="outlined" InputProps={{ readOnly: true }} />
              </Grid>
              <Grid item md={12} xs={12}>
                <TextField fullWidth multiline rows={3} label="Error" margin="dense" value={values.error_message || ''} variant="outlined" InputProps={{ readOnly: true }} />
              </Grid>
            </>
          ) : null}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Cancel
        </Button>
        <Button onClick={handleSave} color="primary" disabled={isSaving}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

ClientScriptDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  mode: PropTypes.oneOf(['client', 'template']).isRequired,
  scriptId: PropTypes.number,
  clientId: PropTypes.number,
  companies: PropTypes.array,
  templates: PropTypes.array,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
};

ClientScriptDialog.defaultProps = {
  scriptId: 0,
  clientId: 0,
  companies: [],
  templates: [],
};

export default ClientScriptDialog;
